import { getApiEndpoint } from './config.js';
import { setToken, clearToken } from '../actions/token.js';
import moment from 'moment';

/**
 * Log in in the API.
 *
 * @param {string} email - The email to use.
 * @param {string} password - The password to use.
 * @param {object} history - The history, for redirecting when user is not logged
 * @param {object} dispatch - The dispatcher for Redux.
 * @param {function} callback - The callback to trigger, after login.
 */
export function login(email, password, history, dispatch, callback) {
  fetch(getApiEndpoint() + '/sessions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: email,
      password: password,
    }),
  }).then(function(response) {
    if (response.status !== 200) {
      callback(false, response);
    } else {
      response.json().then((data) => {
        getUser(data.user_id, data, history, function(userResponse) {
          userResponse.json().then((innerData) => {
            dispatch(setToken(data, innerData));
            callback(true, response);
          });
        });
      });
    }
  });
}

/**
 * Get a user by id.
 *
 * @param {int} userId - The user id of the user to get.
 * @param {string} token - The token to use, for getting user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting user.
 */
export function getUser(userId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Check response for status saying user is not logged in. If not, redirect to
 * login.
 *
 * @param {object} response - The response from the API.
 * @param {object} history - The history object for redirecting.
 * @param {function} callback - The callback to trigger, if user is logged in.
 */
export function checkLogin(response, history, callback) {
  if (response.status === 401) {
    history.push('/login');
    return;
  }

  callback(response);
}

/**
 * Check the validity of the token, before calling the API.
 *
 * @param {object} token - The token object to check.
 * @param {object} history - The history object for redirecting.
 *
 * @return {bool} - Whether or not token is valid.
 */
export function checkToken(token, history) {
  if (token
    && typeof token === "object"
    && token.id !== undefined
    && token.user_id !== undefined
    && token.expires !== undefined
    && moment(token.expires).unix() > moment().unix()) {
    return true;
  }

  history.push('/login');
  return false;
}

/**
 * Save a profile.
 *
 * @param {int} userId - The id of the user.
 * @param {array} fields - The fields to save.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function saveProfile(userId, fields, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: fields,
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Change password for a user.
 *
 * @param {int} userId - The id of the user.
 * @param {string} password - The password to set.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function changePassword(userId, password, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify([{
      op: 'set',
      fields: {
        password: password,
      },
    }]),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Get users
 *
 * @param {int} offset - The offset to get results from.
 * @param {int} limit - The number of conversations to get.
 * @param {object} token - The token to use, for getting conversations.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after getting
 * conversations.
 */
export function getUsers(offset, limit, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }
  var parameters = [];
  if (offset) {
    parameters.push('offset=' + encodeURIComponent(offset));
  }
  if (limit) {
    parameters.push('limit=' + encodeURIComponent(limit));
  }

  fetch(getApiEndpoint() + '/users?' + parameters.join('&'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Delete a user
 *
 * @param {int} userId - The id of the user.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function deleteUser(userId, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Create a user.
 *
 * @param {object} user - The user to create.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function createUser(user, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify(user),
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Get roles.
 *
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function getRoles(token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/roles', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, callback);
  });
}

/**
 * Does the user have a specific permission?
 *
 * @param {int} userId - The id of the user to check for.
 * @param {string} permissionName - The name of the permission.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function hasPermission(userId, permissionName, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId + '/permissions/' + permissionName, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    checkLogin(response, history, (response) => {
      if (response.status === 200) {
        response.json().then((data) => {
          callback(data.allowed, data, response);
          return;
        });
      }

      callback(false, response);
    });
  });
}

/**
 * Does the user have one of the permissions?
 *
 * @param {int} userId - The id of the user to check for.
 * @param {array} permissionNames - The names of the permissions.
 * @param {object} token - The token to use, for saving user.
 * @param {object} history - The history, for redirecting when user is not logged
 * in
 * @param {function} callback - The callback to trigger, after saving user.
 */
export function hasOneOfPermissions(userId, permissionNames, token, history, callback) {
  if (!checkToken(token, history)) {
    return;
  }

  fetch(getApiEndpoint() + '/users/' + userId + '/permissions/check', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
    body: JSON.stringify(permissionNames),
  }).then(function(response) {
    checkLogin(response, history, (response) => {
      if (response.status === 200) {
        response.json().then((data) => {
          var access = false;
          Object.keys(data).forEach((permissionName) => {
            if (data[permissionName]) {
              access = true;
            }
          });
          callback(access, data, response);
          return;
        });
      }

      callback(false, {}, response);
    });
  });
}

/**
 * Log out of the API.
 *
 * @param {object} token - The token to use, for saving user.
 * @param {object} dispatch - The dispatcher for Redux.
 * @param {function} callback - The callback to trigger, after login.
 */
export function logout(token, dispatch, callback) {
  console.log(token);
  fetch(getApiEndpoint() + '/users/' + token.user_id + '/sessions/' + token.id, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Id': token.id,
    },
  }).then(function(response) {
    if (response.status !== 200) {
      callback(true, response);
    } else {
      response.json().then((data) => {
        dispatch(clearToken());
        callback(false, response);
      });
    }
  });
}



// WEBPACK FOOTER //
// ./src/utility/account.js